import { useState } from 'react'
import { Gavel, Clock, ChevronDown, ChevronUp, AlertCircle, Check, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { useAuction } from '@/hooks/useAuction'
import { useAuthStore } from '@/store/useAuthStore'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { formatPrice, formatTimeRemaining } from '@/utils/format'
import type { Listing } from '@/types/listing'

interface BidSectionProps {
  listing: Listing
}

export function BidSection({ listing }: BidSectionProps) {
  const { isAuthenticated, user } = useAuthStore()
  // Sellers can't bid on their own auction (server rejects it) — show them
  // the auction status + history but not the bidding controls.
  const isOwner = isAuthenticated && user?.uid === listing.sellerId
  const { openModal, showToast } = useUIStore()
  const {
    bids,
    currentBid,
    minimumBid,
    bidIncrement,
    bidCount,
    isHighBidder,
    reservePrice,
    reserveMet,
    auctionEnded,
    hasAutoBid,
    userMaxBid,
    placeBid,
    setAutoBid,
    isPlacingBid,
    error,
  } = useAuction({ listing })

  const [bidAmount, setBidAmount] = useState<string>('')
  const [showAutoBid, setShowAutoBid] = useState(false)
  const [autoBidAmount, setAutoBidAmount] = useState<string>('')
  const [showHistory, setShowHistory] = useState(false)

  const handlePlaceBid = async () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }

    const amount = parseFloat(bidAmount)
    if (isNaN(amount) || amount < minimumBid) {
      showToast(`Minimum bid is ${formatPrice(minimumBid)}`, 'error')
      return
    }

    const result = await placeBid(amount)
    if (result.success) {
      showToast(result.message, 'success')
      setBidAmount('')
    } else {
      showToast(result.message, 'error')
    }
  }

  const handleSetAutoBid = async () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }

    const amount = parseFloat(autoBidAmount)
    if (isNaN(amount) || amount < minimumBid) {
      showToast(`Maximum bid must be at least ${formatPrice(minimumBid)}`, 'error')
      return
    }

    // Commitment warning — winning an auction is a binding commitment to buy (bug MB6PJgJt)
    if (!confirm(
      `Set an auto-bid up to ${formatPrice(amount)}?\n\n` +
      `Be careful: if you win, you're committed to buy. We'll automatically ` +
      `bid on your behalf up to this amount.`
    )) return

    const result = await setAutoBid(amount)
    if (result.success) {
      showToast(result.message, 'success')
      setAutoBidAmount('')
      setShowAutoBid(false)
    } else {
      showToast(result.message, 'error')
    }
  }

  const quickBidAmounts = [
    minimumBid,
    minimumBid + bidIncrement,
    minimumBid + bidIncrement * 2,
    minimumBid + bidIncrement * 5,
  ]

  if (auctionEnded) {
    return (
      <div className="card p-4 sm:p-6 bg-slate-50">
        <div className="text-center">
          <Gavel className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold mb-1">Auction Ended</h3>
          {bidCount > 0 ? (
            <>
              <p className="text-2xl font-bold text-slate-900 mb-2">
                Final price: {formatPrice(currentBid)}
              </p>
              {isHighBidder && (
                <p className="text-primary-600 font-medium">
                  Congratulations! You won this auction.
                </p>
              )}
            </>
          ) : (
            <p className="text-slate-500">No bids were placed</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="card p-4 sm:p-6">
      {/* Current bid status */}
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between mb-2">
          {/* Before any bids the figure is the seller's starting price, not a real
              bid — labelling it "Current Bid" misreads as someone having bid. */}
          <span className="text-sm text-slate-500">{bidCount > 0 ? 'Current Bid' : 'Starting price'}</span>
          {listing.expiresAt && (
            <div className="flex items-center gap-1 text-sm text-purple-600">
              <Clock className="w-4 h-4" />
              {formatTimeRemaining(listing.expiresAt)}
            </div>
          )}
        </div>
        <p className="text-2xl sm:text-3xl font-bold text-slate-900">{formatPrice(currentBid)}</p>
        <p className="text-sm text-slate-500">
          {bidCount} {bidCount === 1 ? 'bid' : 'bids'}
          {bidCount > 0 && (listing as { highBidderName?: string }).highBidderName && !isHighBidder && (
            <span className="ml-2">
              • Leading: <span className="text-slate-700 font-medium">{(listing as { highBidderName?: string }).highBidderName}</span>
            </span>
          )}
          {!reserveMet && reservePrice && (
            <span className="text-amber-600 ml-2">• Reserve not met</span>
          )}
        </p>
      </div>

      {/* Bidder status */}
      {isAuthenticated && isHighBidder && (
        <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg mb-4">
          <Check className="w-5 h-5" />
          <span className="font-medium">You are the highest bidder!</span>
        </div>
      )}

      {isAuthenticated && hasAutoBid && userMaxBid && (
        <div className="flex items-center gap-2 p-3 bg-primary-50 text-primary-700 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5" />
          <span>Auto-bid active up to {formatPrice(userMaxBid)}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Bid input + controls — hidden from the listing owner */}
      {!isOwner && (
      <>
      <div className="mb-4">
        <label htmlFor="bid-amount" className="block text-sm font-medium mb-2">
          Your Bid (minimum {formatPrice(minimumBid)})
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
          <input
            id="bid-amount"
            type="number"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            className="input pl-8"
            placeholder={minimumBid.toString()}
            min={minimumBid}
            step={bidIncrement}
            disabled={isPlacingBid}
          />
        </div>
      </div>

      {/* Quick bid buttons */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4">
        {quickBidAmounts.map((amount) => (
          <button
            key={amount}
            onClick={() => setBidAmount(amount.toString())}
            className={clsx(
              'px-2.5 sm:px-3 py-1.5 text-sm rounded-lg border transition-colors',
              bidAmount === amount.toString()
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-slate-200 hover:border-slate-300'
            )}
            disabled={isPlacingBid}
          >
            {formatPrice(amount)}
          </button>
        ))}
      </div>

      {/* Place bid button */}
      <Button
        onClick={handlePlaceBid}
        className="w-full mb-3"
        disabled={isPlacingBid || isHighBidder}
        leftIcon={isPlacingBid ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" />}
      >
        {isPlacingBid ? 'Placing Bid...' : isHighBidder ? 'You\'re Winning!' : 'Place Bid'}
      </Button>

      {/* Auto-bid toggle */}
      <button
        onClick={() => setShowAutoBid(!showAutoBid)}
        className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 py-2"
        aria-expanded={showAutoBid}
      >
        {showAutoBid ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        Set Auto-Bid
      </button>

      {/* Auto-bid form */}
      {showAutoBid && (
        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-600 mb-3">
            We'll automatically bid on your behalf up to your maximum amount, using the minimum
            increment needed to stay ahead.
          </p>
          <div className="relative mb-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <input
              type="number"
              value={autoBidAmount}
              onChange={(e) => setAutoBidAmount(e.target.value)}
              className="input pl-8"
              placeholder="Maximum bid"
              min={minimumBid}
              disabled={isPlacingBid}
            />
          </div>
          <Button
            variant="secondary"
            onClick={handleSetAutoBid}
            className="w-full"
            disabled={isPlacingBid}
          >
            {isPlacingBid ? 'Setting...' : 'Set Auto-Bid'}
          </Button>
        </div>
      )}
      </>
      )}

      {/* Bid history toggle */}
      {bids.length > 0 && (
        <>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 py-2 mt-4 border-t border-slate-200"
            aria-expanded={showHistory}
          >
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Bid History ({bids.length})
          </button>

          {showHistory && (
            <div className="mt-3 max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 text-left">
                    <th scope="col" className="pb-2 font-medium">Bidder</th>
                    <th scope="col" className="pb-2 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {bids.map((bid, index) => (
                    <tr key={bid.id}>
                      <td className={clsx('py-2', index === 0 && 'font-medium')}>
                        {bid.displayName}
                        {bid.isAutoBid && (
                          <span className="text-xs text-slate-400 ml-1">(auto)</span>
                        )}
                      </td>
                      <td className={clsx('py-2 text-right', index === 0 && 'font-medium')}>
                        {formatPrice(bid.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
